"use client";

import { useEffect, useRef, useState } from "react";
import { useScramble } from "use-scramble";

import { cn } from "@/lib/utils";

const NAME_BASE = "truncate text-[13px] font-normal leading-5 transition-colors";
const NAME_IDLE = "text-contrast/70 group-hover/search:text-heading";
const NAME_ACTIVE = "text-heading font-medium";

/**
 * Isolated label so parent re-renders don’t restart useScramble every frame.
 * Each time the text resolves to "Searching", replay runs the scramble → resolve cycle again.
 */
export function ScramblingSearchingLabel() {
  const { ref, replay } = useScramble({
    text: "Searching",
    speed: 0.5,
    tick: 2,
    step: 1,
    scramble: 8,
    seed: 2,
    overdrive: false,
    overflow: true,
    range: [65, 122],
    onAnimationEnd: () => {
      replay();
    },
  });

  return <span ref={ref} className={cn(NAME_BASE, "text-heading")} />;
}

export function PendingSearchText({
  phase,
  name,
  onRevealComplete,
  onShineEnd,
}: {
  phase: "scrambling" | "revealing" | "shining";
  name: string | null;
  onRevealComplete: () => void;
  onShineEnd: () => void;
}) {
  const { ref } = useScramble({
    text: name ?? "Searching",
    speed: 0.5,
    tick: 2,
    step: 1,
    scramble: 6,
    seed: 2,
    overdrive: false,
    overflow: true,
    range: [65, 122],
    onAnimationEnd: name ? onRevealComplete : undefined,
  });

  if (phase === "shining") {
    return (
      <span
        className={cn(
          NAME_BASE,
          "bg-shine text-shine animate-bg-shine-once [--gradient-color:var(--ai)]",
        )}
        onAnimationEnd={onShineEnd}
      >
        {name}
      </span>
    );
  }

  return (
    <span
      ref={ref}
      className={cn(NAME_BASE, name ? "text-ai" : "text-heading")}
    />
  );
}

/** Isolated so list re-renders don’t restart mid-scramble; mount when a rename should animate. */
function SidebarSearchNameScramble({
  name,
  isActive,
}: {
  name: string;
  isActive: boolean;
}) {
  const { ref } = useScramble({
    text: name,
    speed: 1,
    tick: 1,
    step: 2,
    scramble: 3,
    seed: 2,
    overdrive: false,
    overflow: true,
    range: [65, 122],
  });

  return (
    <span
      ref={ref}
      className={cn(NAME_BASE, isActive ? NAME_ACTIVE : NAME_IDLE)}
    />
  );
}

export function SidebarSearchName({
  name,
  isActive,
}: {
  name: string;
  isActive: boolean;
}) {
  const prevNameRef = useRef(name);
  const [scrambleGeneration, setScrambleGeneration] = useState(0);

  useEffect(() => {
    if (prevNameRef.current === name) return;

    prevNameRef.current = name;
    const frame = requestAnimationFrame(() => {
      setScrambleGeneration((generation) => generation + 1);
    });

    return () => cancelAnimationFrame(frame);
  }, [name]);

  if (scrambleGeneration === 0) {
    return (
      <span className={cn(NAME_BASE, isActive ? NAME_ACTIVE : NAME_IDLE)}>
        {name}
      </span>
    );
  }

  return (
    <SidebarSearchNameScramble
      key={scrambleGeneration}
      name={name}
      isActive={isActive}
    />
  );
}
